// Builds a phase-based (not day-by-day) preparation plan from the same
// inputs Account Settings' training-schedule panel already collects --
// assessment/role selection, a weekly training cadence, and a test date --
// so it works identically for a brand-new anonymous visitor and a
// returning, logged-in account. Always recomputed from (preferences,
// history) at render time; nothing here is persisted as its own object
// (see preparationProfiles.js/server /account/preparation-preferences for
// the actual durable state).
import { getServiceProfileModules } from '../data/serviceProfiles';
import { buildTrainingRecommendations, moduleCountForSessionLength } from './trainingRecommendations';
import { buildCommercialTrainingRecommendations, COMMERCIAL_MODULES } from './commercialLiveScoring';
import { buildModuleScoreMap } from './cbatScoring';
import { daysUntilAssessment } from './assessmentBooking';

// Re-exported (not a second copy) so callers importing it from here and the
// journey card's own countdown can never disagree by a day.
export { daysUntilAssessment };

const startOfLocalDay = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());

// Accepts a 'YYYY-MM-DD' local date or any Date-parsable timestamp (e.g. a
// booking's UTC created_at) and returns that moment's local calendar day.
const toLocalDay = (value) => {
  if (!value) return null;
  const parsed = /^\d{4}-\d{2}-\d{2}$/.test(value) ? new Date(`${value}T00:00:00`) : new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : startOfLocalDay(parsed);
};

// Local calendar date, not toISOString() -- that converts to UTC first, so
// local midnight anywhere east of UTC (the UK in summer, Australia always)
// came out as the previous day.
export const toLocalIsoDate = (date) => [
  date.getFullYear(),
  String(date.getMonth() + 1).padStart(2, '0'),
  String(date.getDate()).padStart(2, '0')
].join('-');

// Used whenever no usable test date is available, so the plan still shows
// a complete, real example rather than an empty state.
const DEFAULT_HORIZON_DAYS = 42;
// <= this many days: one final-review-only phase, no room for anything else.
const VERY_SOON_MAX_DAYS = 7;
// <= this many days: two phases (foundation + final-review) -- not enough
// runway for a separate targeted-practice phase to be worth its own block.
const SHORT_WINDOW_MAX_DAYS = 13;
const FOUNDATION_SHARE = 0.35;
const FOUNDATION_MIN_DAYS = 3;
const FOUNDATION_MAX_DAYS = 14;
const FINAL_REVIEW_SHARE = 0.15;
const FINAL_REVIEW_MIN_DAYS = 2;
const FINAL_REVIEW_MAX_DAYS = 7;
const FOUNDATION_MODULE_LIMIT = 6;
const TARGETED_MODULE_LIMIT = 6;
const FINAL_REVIEW_MODULE_LIMIT = 4;

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const addDays = (date, days) => {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
};

// Normalizes both recommendation engines' priority vocabulary down to one
// boolean: is this a module the candidate is already known to be weak on
// (isWeak: true, belongs in Targeted Practice), or is it untested/stale/
// already-fine (isWeak: false, belongs in Foundation's breadth sample)?
// The two engines don't share a vocabulary for this: the military engine's
// 'untested' priority is distinct from 'high'/'medium', but the commercial
// engine tags *both* "never attempted" and "genuinely low-scoring" modules
// as priority 'high' -- only a low-scoring entry carries a numeric
// `stanine`, so that's the real discriminator there.
const rankModulePool = ({ family, serviceProfileId, targetRoleId, history }) => {
  if (family === 'commercial') {
    return buildCommercialTrainingRecommendations(history, { limit: COMMERCIAL_MODULES.length })
      .map((entry) => ({
        moduleId: entry.moduleId,
        module: entry.module,
        reason: entry.reason,
        isWeak: typeof entry.stanine === 'number'
      }));
  }
  const moduleScores = buildModuleScoreMap(history);
  const poolSize = getServiceProfileModules(serviceProfileId).length;
  return buildTrainingRecommendations({ serviceProfileId, targetRoleId, moduleScores, limit: poolSize })
    .map((entry) => ({
      moduleId: entry.moduleId,
      module: entry.module,
      reason: entry.reason,
      isWeak: entry.priority === 'high' || entry.priority === 'medium'
    }));
};

// Both recommendation engines only return modules that are untested, weak,
// or stale -- a module the candidate is already solid on is filtered out
// entirely. That's correct for "what to practice next," but would leave a
// strong performer's plan looking thin or empty. Backfill with the rest of
// the family/profile's module catalog (in catalog order) so every phase
// always has enough material, and Foundation's category-diversity pick
// always has full category coverage to draw from.
const backfillPool = (pool, { family, serviceProfileId }) => {
  const usedIds = new Set(pool.map((entry) => entry.moduleId));
  const fullModules = family === 'commercial' ? COMMERCIAL_MODULES : getServiceProfileModules(serviceProfileId);
  const extras = fullModules
    .filter((module) => !usedIds.has(module.id))
    .map((module) => ({ moduleId: module.id, module, reason: 'Keep this one sharp with regular review', isWeak: false }));
  return [...pool, ...extras];
};

const pickOnePerCategory = (pool, limit) => {
  const seenCategories = new Set();
  const picked = [];
  for (const entry of pool) {
    if (seenCategories.has(entry.module.category)) continue;
    seenCategories.add(entry.module.category);
    picked.push(entry);
    if (picked.length >= limit) break;
  }
  return picked;
};

const splitIntoPhaseWindows = (totalDays) => {
  if (totalDays <= VERY_SOON_MAX_DAYS) {
    return [{ id: 'final-review', startDay: 1, endDay: Math.max(totalDays, 1) }];
  }

  const finalDays = clamp(Math.round(totalDays * FINAL_REVIEW_SHARE), FINAL_REVIEW_MIN_DAYS, FINAL_REVIEW_MAX_DAYS);

  if (totalDays <= SHORT_WINDOW_MAX_DAYS) {
    const splitPoint = Math.max(1, totalDays - finalDays);
    return [
      { id: 'foundation', startDay: 1, endDay: splitPoint },
      { id: 'final-review', startDay: splitPoint + 1, endDay: totalDays }
    ];
  }

  const foundationDays = clamp(Math.round(totalDays * FOUNDATION_SHARE), FOUNDATION_MIN_DAYS, FOUNDATION_MAX_DAYS);
  const targetedDays = totalDays - finalDays - foundationDays;
  return [
    { id: 'foundation', startDay: 1, endDay: foundationDays },
    { id: 'targeted-practice', startDay: foundationDays + 1, endDay: foundationDays + targetedDays },
    { id: 'final-review', startDay: foundationDays + targetedDays + 1, endDay: totalDays }
  ];
};

const PHASE_META = {
  foundation: {
    label: 'Foundation',
    purpose: 'Sample across every practice category so you know where you actually stand before narrowing your focus.'
  },
  'targeted-practice': {
    label: 'Targeted Practice',
    purpose: 'Spend most of your time on the specific modules that need the most work.'
  },
  'final-review': {
    label: 'Final Review',
    purpose: 'Rehearse what you have already covered under realistic conditions. No new material this close to test day.'
  }
};

// Foundation gets breadth (one untested/already-fine module per category --
// never a known weak spot, so Targeted Practice below is never starved of
// its actual reason to exist); Targeted Practice gets the known-weak
// modules first, in priority order, only falling back to leftover breadth
// modules if there aren't enough; Final Review rehearses whatever was
// already introduced rather than new material. This is what keeps the
// phases genuinely different even for a brand-new visitor with zero
// history, where every module would otherwise rank identically as
// "untested".
const assignModulesToPhases = (windows, pool) => {
  if (windows.length === 1) {
    return windows.map((window) => ({ ...window, recommendedModules: pool.slice(0, FOUNDATION_MODULE_LIMIT) }));
  }

  const weakEntries = pool.filter((entry) => entry.isWeak);
  const breadthEntries = pool.filter((entry) => !entry.isWeak);

  const foundationModules = pickOnePerCategory(breadthEntries.length ? breadthEntries : pool, FOUNDATION_MODULE_LIMIT);
  const usedIds = new Set(foundationModules.map((entry) => entry.moduleId));

  const hasTargetedWindow = windows.some((window) => window.id === 'targeted-practice');
  let targetedModules = [];
  if (hasTargetedWindow) {
    const remainingWeak = weakEntries.filter((entry) => !usedIds.has(entry.moduleId));
    const remainingBreadth = breadthEntries.filter((entry) => !usedIds.has(entry.moduleId));
    targetedModules = [...remainingWeak, ...remainingBreadth].slice(0, TARGETED_MODULE_LIMIT);
  }

  const introduced = [...foundationModules, ...targetedModules];
  const finalModules = (introduced.length ? introduced : pool).slice(0, FINAL_REVIEW_MODULE_LIMIT);

  return windows.map((window) => {
    if (window.id === 'foundation') return { ...window, recommendedModules: foundationModules };
    if (window.id === 'targeted-practice') return { ...window, recommendedModules: targetedModules };
    return { ...window, recommendedModules: finalModules };
  });
};

const DATE_NOTICES = {
  missing: { type: 'info', message: 'Add your test date above to personalize this with real dates -- this example plan uses a typical six-week runway.' },
  invalid: { type: 'warning', message: "That date didn't look right, so this example plan uses a typical six-week runway instead." },
  past: { type: 'warning', message: 'That date has already passed, so this example plan uses a typical six-week runway instead -- double check the date above.' }
};

// startDate anchors day 1 (e.g. when the candidate's booking was made) so
// the phases stay fixed in time and today can move through them. Without
// one, day 1 is today -- right for a plan being built right now, but on
// its own it would put a returning candidate back in Foundation forever.
export const buildPreparationPlan = ({
  family = 'military',
  serviceProfileId = 'general',
  targetRoleId = null,
  trainingDaysPerWeek = 4,
  typicalSessionMinutes = 30,
  assessmentDate = null,
  startDate = null,
  history = [],
  now = new Date()
} = {}) => {
  let dateStatus = 'set';
  let totalDays = DEFAULT_HORIZON_DAYS;
  let hasConcreteDate = false;
  const today = startOfLocalDay(now);
  let planStart = today;

  if (!assessmentDate) {
    dateStatus = 'missing';
  } else {
    const diff = daysUntilAssessment(assessmentDate, now);
    if (diff === null) {
      dateStatus = 'invalid';
    } else if (diff < 0) {
      dateStatus = 'past';
    } else {
      dateStatus = 'set';
      hasConcreteDate = true;
      const anchor = toLocalDay(startDate);
      if (anchor && anchor.getTime() <= today.getTime()) planStart = anchor;
      totalDays = Math.max(daysUntilAssessment(assessmentDate, planStart), 1);
    }
  }

  const rankedPool = backfillPool(
    rankModulePool({ family, serviceProfileId, targetRoleId, history }),
    { family, serviceProfileId }
  );
  const windows = splitIntoPhaseWindows(totalDays);
  const modulesPerSession = moduleCountForSessionLength(typicalSessionMinutes);

  const phases = assignModulesToPhases(windows, rankedPool).map((window) => ({
    id: window.id,
    label: PHASE_META[window.id].label,
    purpose: PHASE_META[window.id].purpose,
    startDay: window.startDay,
    endDay: window.endDay,
    dateRange: hasConcreteDate ? {
      start: toLocalIsoDate(addDays(planStart, window.startDay - 1)),
      end: toLocalIsoDate(addDays(planStart, window.endDay - 1))
    } : null,
    sessionsPerWeek: trainingDaysPerWeek,
    sessionMinutes: typicalSessionMinutes,
    modulesPerSession,
    recommendedModules: window.recommendedModules
  }));

  return {
    dateStatus,
    notice: DATE_NOTICES[dateStatus] || null,
    hasConcreteDate,
    startDate: hasConcreteDate ? toLocalIsoDate(planStart) : null,
    totalDays,
    totalWeeks: Math.max(1, Math.round(totalDays / 7)),
    phases
  };
};

// Where today falls in a dated plan: the phase covering it, how far into
// that phase, and what comes next. null for an undated plan, or when today
// is outside it (test day itself comes after the plan's last day).
export const locateTodayInPlan = (plan, now = new Date()) => {
  if (!plan?.hasConcreteDate || !plan.startDate) return null;
  const start = toLocalDay(plan.startDate);
  const dayNumber = Math.round((startOfLocalDay(now).getTime() - start.getTime()) / 86400000) + 1;
  const phaseIndex = plan.phases.findIndex((phase) => dayNumber >= phase.startDay && dayNumber <= phase.endDay);
  if (phaseIndex < 0) return null;
  const phase = plan.phases[phaseIndex];
  return {
    phase,
    phaseIndex,
    phaseCount: plan.phases.length,
    nextPhase: plan.phases[phaseIndex + 1] || null,
    dayOfPhase: dayNumber - phase.startDay + 1,
    phaseLength: phase.endDay - phase.startDay + 1
  };
};
